import React, { useMemo } from 'react';
import { Card, Table, Button, Space, Tag, Typography, Select, Input } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import stockAdjustmentService, { type StockAdjustment } from '@/services/stockAdjustment.service';

const { Title, Text } = Typography;

export const StockAdjustmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = React.useState<string | undefined>();
  const [search, setSearch] = React.useState('');

  const { data: rows = [], isLoading, refetch } = useQuery({
    queryKey: ['stock-adjustments', status, search],
    queryFn: () =>
      stockAdjustmentService.list({
        ...(status ? { status } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
      }),
  });

  const columns: ColumnsType<StockAdjustment> = useMemo(
    () => [
      { title: 'Number', dataIndex: 'adjustment_number', key: 'n', width: 160 },
      { title: 'Date', dataIndex: 'adjustment_date', key: 'd', width: 120 },
      {
        title: 'Warehouse',
        key: 'w',
        render: (_: unknown, r) => r.warehouse?.warehouse_name ?? r.warehouse_id,
      },
      { title: 'Type', dataIndex: 'adjustment_type', key: 't', width: 140 },
      {
        title: 'Status',
        dataIndex: 'adjustment_status',
        key: 's',
        width: 120,
        render: (s: string) => {
          const color =
            s === 'Posted' ? 'green' : s === 'Cancelled' ? 'default' : s === 'Approved' ? 'blue' : 'gold';
          return <Tag color={color}>{s}</Tag>;
        },
      },
      {
        title: '',
        key: 'a',
        width: 100,
        render: (_: unknown, r) => (
          <Button type="link" onClick={() => navigate(`/scm/warehouse/stock-adjustment/${r.id}`)}>
            Open
          </Button>
        ),
      },
    ],
    [navigate]
  );

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
            <div>
              <Title level={4} style={{ margin: 0 }}>
                Stock adjustments
              </Title>
              <Text type="secondary">Warehouse-level corrections with approval and posting to stock ledger.</Text>
            </div>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/scm/warehouse/stock-adjustment/new')}>
              New adjustment
            </Button>
          </Space>
          <Space wrap>
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 160 }}
              value={status}
              onChange={setStatus}
              options={[
                { value: 'Draft', label: 'Draft' },
                { value: 'Submitted', label: 'Submitted' },
                { value: 'Approved', label: 'Approved' },
                { value: 'Posted', label: 'Posted' },
                { value: 'Cancelled', label: 'Cancelled' },
              ]}
            />
            <Input.Search
              placeholder="Search number / reference"
              allowClear
              style={{ width: 260 }}
              onSearch={() => refetch()}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Space>
          <Table<StockAdjustment>
            rowKey="id"
            loading={isLoading}
            columns={columns}
            dataSource={rows}
            pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
          />
        </Space>
      </Card>
    </div>
  );
};

export default StockAdjustmentsPage;
